import * as paymentResolvers from "./paymentResolvers";
import { extractBrokerFromPeople } from "./people";
import * as towerResolvers from "./towerResolvers";
import { roundToNearestDollar } from "common/numbers";
import * as strings from "common/strings";
import * as layerSupport from "domain/layerSupport";
import * as uuid from "uuid";

export const resolvePremiumAdjustment = (
  premiumAdjustment,
  { layer, program, people }
) => {
  if (!premiumAdjustment) {
    return null;
  }

  const shareOfLimitFractionOfLimit =
    layer.shareOfLine != null
      ? 0.01 * layer.shareOfLine
      : layer.limit != null && layer.lineSize != null && layer.limit !== 0
      ? layer.lineSize / layer.limit
      : null;

  const grossPremiumChange = premiumAdjustment.grossPremiumChange ?? 0;
  const defaultShareOfGrossPremiumChange =
    shareOfLimitFractionOfLimit != null
      ? grossPremiumChange * shareOfLimitFractionOfLimit
      : null;

  const broker = premiumAdjustment.broker ?? extractBrokerFromPeople(people);

  premiumAdjustment = {
    type: "Additional Premium",
    startDate: layer.inception ?? program.inception,
    endDate: program.expiration,
    grossPremiumChange: 0,
    ...premiumAdjustment,
    broker,
    shareOfGrossPremiumChange:
      premiumAdjustment.shareOfGrossPremiumChange ??
      defaultShareOfGrossPremiumChange,
  };

  premiumAdjustment.paymentTerms = paymentResolvers.resolvePaymentTerms(
    premiumAdjustment.paymentTerms ?? layer?.paymentTerms,
    {
      fromDateString: premiumAdjustment.startDate,
    }
  );

  return premiumAdjustment;
};

export const resolvePricing = (pricing) => {
  if (!pricing) {
    return pricing;
  }

  if (
    pricing.defaultModel &&
    (!pricing.selectedModel ||
      (pricing.prices && !(pricing.selectedModel in pricing.prices)))
  ) {
    return {
      ...pricing,
      selectedModel: pricing.defaultModel,
    };
  } else {
    return pricing;
  }
};

// A layer default is a program-wide fallback, so a layer that carries the key
// with an empty value (cleared in the UI, or written by a schema upgrade) must
// still fall back to the default instead of shadowing it.
const applyLayerDefaults = (layer, layerDefaults) => {
  const defaults = layerDefaults ?? {};
  const out = { ...defaults, ...layer };
  for (const key of Object.keys(defaults)) {
    out[key] = layer?.[key] ?? defaults[key];
  }
  return out;
};

export const resolveLayer = (
  layer,
  { program, people, claims, layerDefaults, roundPricing = false }
) => {
  const paymentTerms = paymentResolvers.resolvePaymentTerms(
    layer.paymentTerms ?? program.paymentTerms,
    { fromDateString: program.effectiveDate ?? program.inception }
  );

  const status = layer.status ?? "DRAFT";

  const includedSir = layer?.includeSir
    ? claims?.selfInsuredRetentions?.find(
        (sir) => sir.id === layer.includeSir
      ) ?? null
    : null;

  const sir = includedSir?.value ?? null;

  const withDefaults = applyLayerDefaults(layer, layerDefaults);
  if (withDefaults.paperProvider === layerSupport.NONE_PAPER_PROVIDER) {
    // An explicit "None" beats the layer default and resolves to no provider.
    withDefaults.paperProvider = null;
  }

  const out = {
    ...withDefaults,
    paymentTerms,
    status,
    bound: status === "BOUND",
    brokerage: layer.brokerage ?? program.brokerage ?? 0,
    otherAcquisitionCosts:
      layer.otherAcquisitionCosts ?? program.otherAcquisitionCosts ?? 0,
    slipFee: layer.slipFee ?? 0,
    aggregateLimit: layer.aggregateLimit ?? layer.limit,
    sir,
    effectiveAttachment:
      layer?.attachment != null ? layer.attachment + (sir ?? 0) : null,
    profit: layer.profit ?? program.profit ?? 0,
    expenses: layer.expenses ?? program.expenses ?? 0,
    grossPPM: _layerToGrossPPM(layer),
    grossPremium: _layerToGrossPremium(layer),
    shareOfLine: _layerToShareOfLine(layer),
    lineSize: _layerToLineSize(layer),
    shareOfPremium: _layerToShareOfPremium(layer),
    shortName: layerSupport.shortLayerName(layer),
    puniWrapBrokeragePct: layer.puniWrapBrokeragePct ?? 0,
    transaction: layer.transaction ?? program.transaction,
  };

  out.shortShareName = layerSupport.shortShareName(out);

  for (const key of [
    "quote",
    "binder",
    "policy",
    "puniQuote",
    "puniBinder",
    "puniPolicy",
  ]) {
    if (out[key] != null) {
      out[key] = _makeDocument(out[key]);

      const legacyKey = {
        binder: "isBinderIssued",
        policy: "isPolicyIssued",
      }[key];
      if (legacyKey != null) {
        out[legacyKey] = out[key]?.isIssued;
      }
    }
  }

  out.tac = out.brokerage + out.otherAcquisitionCosts + out.slipFee;

  if (program.targetTotalExpenses != null && layer.expenses == null) {
    const otherExpenses = out.brokerage + out.slipFee;
    out.expenses = program.targetTotalExpenses - otherExpenses;
  }

  if (out.puniWrapPct != null) {
    out.puniWrap =
      Number.isFinite(out.puniWrapPct) && Number.isFinite(out.grossPremium)
        ? out.grossPremium * (out.puniWrapPct / 100)
        : null;

    out.puniWrapBrokerage =
      Number.isFinite(out.puniWrap) && Number.isFinite(out.puniWrapBrokeragePct)
        ? out.puniWrap * (out.puniWrapBrokeragePct / 100)
        : null;

    out.netPuniWrap =
      Number.isFinite(out.puniWrap) && Number.isFinite(out.puniWrapBrokerage)
        ? out.puniWrap - out.puniWrapBrokerage
        : null;

    out.shareOfPuniWrap =
      Number.isFinite(out.shareOfLine) && Number.isFinite(out.puniWrap)
        ? out.puniWrap * (out.shareOfLine / 100)
        : null;

    out.shareOfPremiumExPuniWrap = out.isPuniWrapIncluded
      ? Number.isFinite(out.shareOfPuniWrap) &&
        Number.isFinite(out.shareOfPremium)
        ? out.shareOfPremium - out.shareOfPuniWrap
        : null
      : out.shareOfPremium;

    out.shareOfPuniWrapBrokerage =
      Number.isFinite(out.shareOfPuniWrap) &&
      Number.isFinite(out.puniWrapBrokeragePct)
        ? out.shareOfPuniWrap * (out.puniWrapBrokeragePct / 100)
        : null;
  }

  if (
    layer.limit != null &&
    layer.quotedLineSize != null &&
    layer.quotedGrossPremium != null
  ) {
    out.quotedShareOfPremium =
      layer.quotedGrossPremium * (layer.quotedLineSize / layer.limit);
  }

  out.premiumAdjustments = (layer.premiumAdjustments ?? []).map(
    (premiumAdjustment) =>
      resolvePremiumAdjustment(premiumAdjustment, {
        layer: out,
        program,
        people,
      })
  );

  if (out.triaStatus == null) {
    if (out.isTriaIncluded != null) {
      out.triaStatus = out.isTriaIncluded ? "INCLUDED" : "NOT_INCLUDED";
    } else if (out.TRIA != null) {
      out.triaStatus = out.TRIA === 0 ? "NOT_INCLUDED" : "INCLUDED";
    } else {
      out.triaStatus = "NOT_INCLUDED";
    }
  }
  out.TRIA = out.TRIA ?? out.defaultTria;
  // Clients have requested that when TRIA is rejected we should show the TRIA % as 0. This is primarily because
  // some downstream systems use that TRIA % and when non-zero assume that means that there is TRIA included.
  if (out.triaStatus === "NOT_INCLUDED") {
    out.TRIA = 0;
  }
  out.TRIAAmount = _layerToTRIAAmount(out);
  out.shareOfPremiumWithTRIA = _layerToShareOfPremiumWithTRIA(out);

  if (out.pricing) {
    out.pricing = resolvePricing(out.pricing);
  }

  if (out.sublimit && Array.isArray(out.sublimit) && out.limit != null) {
    out.sublimit = out.sublimit.map((sublimit) => {
      if (sublimit.percentage != null) {
        const calculatedValue = parseFloat(
          (out.limit * sublimit.percentage) / 100
        );
        return {
          ...sublimit,
          value: calculatedValue,
        };
      }
      return sublimit;
    });
  }

  if (roundPricing) {
    out.grossPremium = roundToNearestDollar(out.grossPremium);
    out.shareOfPremium = roundToNearestDollar(out.shareOfPremium);
    // TRIA is charged on the whole-dollar premium, so it is taken from the
    // rounded share of premium: Our Premium 12,350 at 1% shows TRIA 124, not
    // the 123 that rounding 1% of an unrounded 12,349.80 would give.
    out.TRIAAmount = roundToNearestDollar(_layerToTRIAAmount(out));
    out.quotedGrossPremium = roundToNearestDollar(out.quotedGrossPremium);
    out.quotedShareOfPremium = roundToNearestDollar(out.quotedShareOfPremium);
    out.premOpsPremium = roundToNearestDollar(out.premOpsPremium);
    out.pcoPremium = roundToNearestDollar(out.pcoPremium);
    out.liquorPremium = roundToNearestDollar(out.liquorPremium);
    out.autoPremium = roundToNearestDollar(out.autoPremium);

    out.premiumAdjustments = (out.premiumAdjustments ?? []).map((adjustment) =>
      adjustment == null
        ? adjustment
        : {
            ...adjustment,
            grossPremiumChange: roundToNearestDollar(
              adjustment.grossPremiumChange
            ),
            shareOfGrossPremiumChange: roundToNearestDollar(
              adjustment.shareOfGrossPremiumChange
            ),
          }
    );

    // PUNI-wrap fields only exist when the puniWrapPct block ran; round and
    // re-derive the ex-PUNI decomposition against the rounded parts only within
    // that guard.
    if (out.puniWrapPct != null) {
      out.puniWrap = roundToNearestDollar(out.puniWrap);
      out.puniWrapBrokerage = roundToNearestDollar(out.puniWrapBrokerage);
      out.netPuniWrap =
        Number.isFinite(out.puniWrap) && Number.isFinite(out.puniWrapBrokerage)
          ? out.puniWrap - out.puniWrapBrokerage
          : null;
      out.shareOfPuniWrapBrokerage = roundToNearestDollar(
        out.shareOfPuniWrapBrokerage
      );
      out.shareOfPuniWrap = roundToNearestDollar(out.shareOfPuniWrap);
      out.shareOfPremiumExPuniWrap = out.isPuniWrapIncluded
        ? Number.isFinite(out.shareOfPuniWrap) &&
          Number.isFinite(out.shareOfPremium)
          ? out.shareOfPremium - out.shareOfPuniWrap
          : null
        : out.shareOfPremium;
    }

    // Derived additive: recompute from the rounded parts, never from a raw total.
    out.shareOfPremiumWithTRIA = _triaStatusGrossesUp(out.triaStatus)
      ? out.shareOfPremium + out.TRIAAmount
      : out.shareOfPremium;
  }

  return out;
};

const _layerToGrossPPM = (layer) => {
  if (layer.grossPPM == null) {
    if (layer.limit == null || layer.grossPremium == null) {
      return null;
    }
    return (layer.grossPremium / layer.limit) * 1e6;
  }
  return layer.grossPPM;
};

const _layerToGrossPremium = (layer) => {
  if (layer.grossPremium == null) {
    if (layer.limit == null || layer.grossPPM == null) {
      return null;
    }
    return (layer.grossPPM / 1e6) * layer.limit;
  }
  return layer.grossPremium;
};

const _layerToShareOfLine = (layer) => {
  if (layer.shareOfLine == null) {
    if (layer.limit == null || layer.lineSize == null) {
      return null;
    }
    return (layer.lineSize / layer.limit) * 100;
  }
  return layer.shareOfLine;
};

const _layerToLineSize = (layer) => {
  if (layer.lineSize == null) {
    if (layer.limit == null || layer.shareOfLine == null) {
      return null;
    }
    return (layer.shareOfLine / 100) * layer.limit;
  }
  return layer.lineSize;
};

export const _layerToShareOfPremium = (layer) => {
  if (layer.shareOfPremium != null) {
    return layer.shareOfPremium;
  }
  const grossPremium = _layerToGrossPremium(layer);
  const shareOfLine = _layerToShareOfLine(layer);
  if (grossPremium == null) {
    return null;
  }
  if (shareOfLine == null) {
    return null;
  }
  return (grossPremium * shareOfLine) / 100;
};

// INCLUDED_GROSS_UP is a distinct status (not a flag-switched INCLUDED) so the
// gross-up travels with the layer's own data: every resolver run — browser,
// selectors lambda, anywhere — computes the same figure, and toggling a
// company's UI options can never change the numbers on existing layers.
const _triaStatusGrossesUp = (triaStatus) =>
  triaStatus === "GROSS_UP" || triaStatus === "INCLUDED_GROSS_UP";

const _layerToTRIAAmount = (layer) => {
  const shareOfPremium = _layerToShareOfPremium(layer);
  if (shareOfPremium == null) {
    return null;
  }

  if (layer.TRIA == null) {
    return null;
  }
  const triaRate = layer.TRIA / 100;

  if (
    layer.triaStatus === "INCLUDED" ||
    _triaStatusGrossesUp(layer.triaStatus)
  ) {
    return shareOfPremium * triaRate;
  } else {
    return 0;
  }
};

export const _layerToShareOfPremiumWithTRIA = (layer) => {
  const shareOfPremium = _layerToShareOfPremium(layer);
  if (shareOfPremium == null) {
    return null;
  }
  if (_triaStatusGrossesUp(layer.triaStatus)) {
    const triaAmount = _layerToTRIAAmount(layer) ?? 0;
    return shareOfPremium + triaAmount;
  } else {
    return shareOfPremium;
  }
};

const _makeDocument = (input) => {
  if (!input) {
    return input;
  }
  const isIssued = input?.isIssued ?? strings.hasValue(input?.issueDate);
  return {
    ...input,
    isIssued,
    issueDate: isIssued ? input?.issueDate : null,
  };
};

export const extractLayersFromTowerLayers = (towerLayers) => {
  if (!towerLayers?.length) {
    return [];
  }

  const extractFieldsFromTowerLayer = (towerLayer) =>
    Object.fromEntries(
      Object.entries(towerLayer ?? {}).filter(([key]) =>
        ["limit", "attachment"].includes(key)
      )
    );

  const extractFieldsFromTowerShare = (towerShare) =>
    Object.fromEntries(
      Object.entries(towerShare ?? {})
        .filter(([key]) =>
          [
            "carrier",
            "coverage",
            "brokerage",
            "retroactiveDate",
            "shareOfLimit",
            "shareOfGrossPremium",
          ].includes(key)
        )
        .map(([key, value]) => {
          if (key === "shareOfLimit") {
            return ["lineSize", value];
          } else if (key === "shareOfGrossPremium") {
            return ["grossPremium", value];
          } else {
            return [key, value];
          }
        })
    );

  return towerLayers.flatMap((towerLayer, towerLayerIndex) => {
    const layerIdNamespace =
      towerLayer?.id || uuid.v5(`layer-${towerLayerIndex}`, uuid.NIL);
    if (towerLayer?.shares?.length) {
      const resolvedShares = towerResolvers.resolveShares(towerLayer.shares, {
        layer: towerLayer,
      });
      return resolvedShares.map((towerShare, towerShareIndex) => {
        const makeLayerId = () => {
          try {
            return uuid.v5(
              towerShare?.id || `share-${towerShareIndex}`,
              layerIdNamespace
            );
          } catch {
            return uuid.v5(
              towerShare?.id || `share-${towerShareIndex}`,
              uuid.NIL
            );
          }
        };

        return {
          ...extractFieldsFromTowerLayer(towerLayer),
          ...extractFieldsFromTowerShare(towerShare),
          id: makeLayerId(),
        };
      });
    } else {
      return [
        {
          ...extractFieldsFromTowerLayer(towerLayer),
          id: layerIdNamespace,
        },
      ];
    }
  });
};
